import type { Option, Unsubscribe } from "@tento-lona/sheets";
import { type ListenerErrorHandler, ListenerErrorHandlers } from "./client-seams.ts";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import type { SheetHandle } from "./sheet-handle.ts";
import { rowsSideSheetOf } from "../internal/doors.ts";
import { Network } from "../network.ts";
import { CellId, RowId, RowNetworkKey } from "@tento-lona/sheets";
import { RowKey } from "@tento-lona/sheets";
import { type CellValue } from "@tento-lona/sheets";
import { type CellCardinality, isCanonicalKey, SourceRow } from "@tento-lona/sheets/platform";
import {
  CellStoreAddress,
  resolveCellStoreAddress,
} from "../cells/cell-store-address.ts";
import { CellStoreView } from "../cells/cell-store-view.ts";
import type { RowCellCache } from "../sync/row-cell-cache.ts";
import type { LocalCellValue, RowData } from "../sync/row-data.ts";
import type { CellAccessor } from "../cells/sheet-cell-accessor.ts";
import { nonnull } from "@tento-core/assert";
import { FetchedRowIndex } from "./fetched-row-index.ts";
import { PartialDateMemo } from "@tento-lona/sheets/internal-sdk";
// The rows-side stores are private to `@tento/lona/sheets`; the local-edit
// chokepoint and the server-fetch ingest write the sheet-local cell store
// (revision capture, install brackets, cardinality) through the package's
// SDK door, `sheetWiring(sheet).localCells` — its one writable store.
import { type SheetWideRead, type SheetWiring, sheetWiring } from "@tento-lona/sheets/internal-sdk";
import { SheetKey } from "./sheet-key.ts";
import { sheetRowOf } from "../rows/sheet-row.ts";
import type { RowSyncState } from "../sync/sync-poll-entry.ts";
import { type ModelRowReads, RowLocalId, RowKind } from "@tento-lona/sheets";
import {
  Actor,
  type InvalidationSink,
  ColumnDataChanged,
  RowDataChanged,
  RowLegendChanged,
  RowRangeCovered,
  RowRangeDataChanged,
} from "../invalidation.ts";

/**
 * The rows-side per-sheet cell store, as the SDK door hands it out:
 * `@tento/lona/sheets` keeps its store classes off the public barrel (see its
 * `lib.ts` "Stores are internal" note).
 */
type SheetLocalCellStore = SheetWiring["localCells"];

/** One local cell write: a value to paint, or the cell's removal. */
type LocalCellWrite =
  | { readonly clear: false; readonly data: LocalCellValue }
  | { readonly clear: true };

/**
 * Sheet lifecycle manager. Manages a registry of active SheetHandles and
 * forwards cell data / sync state notifications to an InvalidationSink.
 *
 * Replaces SheetModelAdapter — all cell data access goes through each
 * handle's full cell accessor, handed over at `registerSheet` (the handle
 * itself exposes only the consumer slice, `SheetHandle.cells`).
 */
export class SheetDataSource {
  static readonly ACTOR = new Actor("sheet-data-source");
  static readonly SERVER_FETCH_ACTOR = new Actor("server-fetch-ingest");
  readonly sheetHandles = new Map<string, SheetHandle>();
  readonly #sheetUnsubs = new Map<string, () => void>();
  /** Each registered handle's full cell accessor (see `registerSheet`). */
  readonly #accessors = new WeakMap<SheetHandle, CellAccessor>();
  /**
   * Per handle whose backend answers from a seed taken at open (an ephemeral
   * sheet's `SeededSyncBackend`): the rows store and its local-write revision
   * at open (`holdSeedRevision`).
   */
  readonly #seedRevisions = new WeakMap<SheetHandle, { readonly store: object; readonly since: number }>();

  readonly #resolveHandle: Option<
      (rowId: RowId) => RowCellCache
    >;
  /** The store bridges' resolver: the backend's existing cache for a key, building none. */
  readonly #peekHandle: (rowId: RowId) => Option<RowCellCache>;
  readonly #idRegistry: RowLocalId.Registry;
  readonly #sink: InvalidationSink;
  /** Where a store bridge reports what a cache listener threw. */
  readonly #listenerErrors: ListenerErrorHandler;
  /** The server-fetch ingest's date parses, one per distinct date string (judge P8). */
  readonly #dates = new PartialDateMemo(PartialDateMemo.CAPACITY);
  constructor(
    sink: InvalidationSink,
    idRegistry: RowLocalId.Registry,
    /** Backend-registry handle resolver (client-scoped). */
    resolveHandle: Option<
      (rowId: RowId) => RowCellCache
    >,
    /** Where what a store bridge's cache listeners threw goes (the client's
     *  `ClientConfig.listenerErrorHandler`); `ListenerErrorHandlers.RETHROW`
     *  for a data source no client owns. */
    listenerErrors: ListenerErrorHandler = ListenerErrorHandlers.RETHROW,
    /** The store bridges' resolver: the backend's cache for a key if one
     *  exists, building none (`peekRowHandle`). A cache no sheet binds never
     *  goes idle, so a bridge that built one per written key kept them all;
     *  a cache built later reads the store through the client's cell
     *  authority. A data source no client owns bridges through
     *  `resolveHandle`, as before. */
    peekHandle: (rowId: RowId) => Option<RowCellCache> = (rowId) => resolveHandle?.(rowId) ?? null,
  ) {
    this.#peekHandle = peekHandle;
    this.#listenerErrors = listenerErrors;
    this.#sink = sink;
    this.#idRegistry = idRegistry;
    this.#resolveHandle = resolveHandle;
  }

  /** Client-shared rows-side canonical cell store (attached late). */
  #canonicalStore: Option<SourceRow.CellDataStore<string>> = null;
  #canonicalBridgeUnsub: Option<() => void> = null;
  #ordinaryStore: Option<SourceRow.CellDataStore<RowLocalId>> = null;
  #ordinaryBridgeUnsub: Option<Unsubscribe> = null;

  /** One authority feed serves ordinary caches with zero or many open sheets. */
  attachOrdinaryStore(store: SourceRow.CellDataStore<RowLocalId>): void {
    this.#ordinaryStore = store;
    this.#ordinaryBridgeUnsub?.();
    const bridgeTo = this.#peekHandle;
    this.#ordinaryBridgeUnsub = CellStoreView.link(store,
      id => bridgeTo(RowId.fromLocalId(id)),
      SheetDataSource.#BRIDGE_ACTOR, SheetDataSource.SERVER_FETCH_ACTOR, this.#listenerErrors);
  }
  /** Fallback actor for bridge mirrors of untagged writes. */
  static readonly #BRIDGE_ACTOR = new Actor("canonical-bridge");

  /**
   * Attach the client-shared canonical cell store and wire the
   * canonical store→handle bridge: every write to `canonicalCells`
   * (chokepoint edits, facade `put`s, provider installs) mirrors into
   * the backend-registry RowCellCache for that key, so handle-side
   * readers (custom-cell renderers, standalone clients) see it
   * without a refetch. Works with zero open sheets. Wired once, by the
   * client's constructor, over its rows accessor (`ClientConfig.rowsOptions`).
   */
  attachCanonicalStore(store: SourceRow.CellDataStore<string>): void {
    if (this.#canonicalStore === store) return;
    this.#canonicalStore = store;
    this.#canonicalBridgeUnsub?.();
    const resolveHandle = this.#resolveHandle;
    if (!resolveHandle) {
      this.#canonicalBridgeUnsub = null;
      return;
    }
    // The store's change feed fires the key's cache (`CellStoreView.link`):
    // one fire per cache per store burst — the batched weather cells cross
    // here. A key resolves to the backend's existing cache (`peekHandle`;
    // none is built for a key no reader holds). A cache a sheet holds reads
    // the store itself; one no sheet holds copies the change in
    // (`RowCellCache.storeChanged`).
    const bridgeTo = this.#peekHandle;
    this.#canonicalBridgeUnsub = CellStoreView.link<string>(
      store,
      (key) => bridgeTo(RowId.of(key)),
      SheetDataSource.#BRIDGE_ACTOR,
      null,
      this.#listenerErrors,
    );
  }

  /** Client teardown releases both authority feeds. */
  dispose(): void {
    this.#canonicalBridgeUnsub?.(); this.#canonicalBridgeUnsub = null;
    this.#ordinaryBridgeUnsub?.(); this.#ordinaryBridgeUnsub = null;
  }

  // ── Sheet lifecycle ──────────────────────────────────────────────────

  /**
   * Register an open sheet. `cells` is the handle's full cell accessor —
   * `SheetHandle.parse` passes its own; a test passes
   * `sheetHandleInternals(handle).cells`.
   */
  registerSheet(key: SheetKey, handle: SheetHandle, cells: CellAccessor): void {
    if (this.sheetHandles.get(key.mapKey) === handle) return;
    this.#sheetUnsubs.get(key.mapKey)?.();
    this.sheetHandles.set(key.mapKey, handle);
    this.#accessors.set(handle, cells);

    this.#cellsOf(handle).onSyncStateChange.addListener(this, (localId) => {
      if (localId) this.#sink.emit(new RowLegendChanged(SheetDataSource.ACTOR, localId, null, null));
    });

    // Build a parent map so child cell changes also invalidate the parent row.
    const parentMap = new Map<RowLocalId, RowLocalId>();
    for (const row of handle.rows.peekAll()) {
      const localId = this.#idRegistry.resolve(RowKey.of(row.id.toString()));
      const children = (sheetRowOf(row).raw as Network.SheetRow).children;
      if (children) {
        for (const childId of children) {
          const childLocalId = this.#idRegistry.resolve(RowKey.of(childId.toString()));
          parentMap.set(childLocalId, localId);
        }
      }
    }

    // A fetch names the dates it installed; the event carries them so the
    // renderer can leave rendered columns outside that window alone. A
    // write or an explicit invalidation has no range and stays whole-row.
    const rowChanged = (localId: RowLocalId, range: Option<NaiveDate.Range>) =>
      range
        ? new RowRangeDataChanged(SheetDataSource.ACTOR, localId, range)
        : new RowDataChanged(SheetDataSource.ACTOR, localId, null);
    const unsub = this.#cellsOf(handle).onChangeWithOrigin((staleRows, range, origin) => {
      // A read that first covered `range` and carried no cells changed no
      // cell: it is relayed as the coverage it is, to the row and its parent.
      if (origin.kind === "coverage" && range != null) {
        for (const localId of staleRows) {
          this.#sink.emit(new RowRangeCovered(SheetDataSource.ACTOR, localId, range));
          const parentId = parentMap.get(localId);
          if (parentId) this.#sink.emit(new RowRangeCovered(SheetDataSource.ACTOR, parentId, range));
        }
        return;
      }
      for (const localId of staleRows) {
        // Dated physical changes already name the columns. A native timepoint
        // or any accessor change still needs its row-range relay.
        if (origin.kind !== "native-store" || !origin.certifiedDateRows.has(localId.toString())) {
          this.#sink.emit(rowChanged(localId, range));
        }
        const parentId = parentMap.get(localId);
        if (parentId) {
          this.#sink.emit(rowChanged(parentId, range));
        }
      }
    });

    this.#sheetUnsubs.set(key.mapKey, () => {
      unsub();
      this.#cellsOf(handle).onSyncStateChange.removeListener(this);
    });
  }

  /**
   * A closing sheet's teardown: drop every registration whose accessor is
   * `cells` — its change subscription and its entry — whatever key it was
   * registered under (a promote re-keys the handle). Without it the last
   * sheet opened under each key stayed registered, and reachable from the
   * client, after it closed.
   */
  unregisterAccessor(cells: CellAccessor): void {
    for (const [mapKey, handle] of [...this.sheetHandles]) {
      if (this.#accessors.get(handle) !== cells) continue;
      this.#sheetUnsubs.get(mapKey)?.();
      this.#sheetUnsubs.delete(mapKey);
      this.sheetHandles.delete(mapKey);
    }
  }

  unregisterSheet(key: SheetKey): void {
    this.#sheetUnsubs.get(key.mapKey)?.();
    this.#sheetUnsubs.delete(key.mapKey);
    this.sheetHandles.delete(key.mapKey);
  }

  // ── Sync state ───────────────────────────────────────────────────────

  peekSyncState(rowId: RowLocalId): Option<RowSyncState.Detail> {
    for (const handle of this.sheetHandles.values()) {
      const state = this.#cellsOf(handle).peekSyncState(RowNetworkKey.of(rowId));
      if (state) return state;
    }
    return null;
  }

  // ── Prefetch ─────────────────────────────────────────────────────────

  /**
   * The row-keyed local-edit chokepoint.
   *
   * Apply a local/optimistic cell edit so EVERY surface that renders
   * `rowKey` sees it without a refetch — the categorical fix for the
   * split-store bug class. Rows are identified by row key / canonical
   * lookup key, never by product type:
   *
   * 1. Canonical keys (`:~…`) write the client-shared rows-side
   *    `canonicalCells` store once. Its subscriber (sheets/sheet.ts)
   *    re-emits `ColumnDataChanged` per open sheet, dirtying facades
   *    (TASKS_FACADE, EVENTS_FACADE, …). Works with zero open sheets.
   * 2. Non-canonical keys write each matching open sheet's
   *    `sourceCellData` (matched by `row.id` or `row.lookupKey`); the
   *    store emits its own `ColumnDataChanged`.
   * 3. The backend-registry RowCellCache for the cell-access key gets
   *    the same edit, so handle-side readers (custom-cell renderers,
   *    standalone clients) update too.
   * 4. `RowDataChanged` fires per matched row for legend/sidebar
   *    consumers.
   *
   * Which of 1-3 applies is not decided inline: `resolveCellStoreAddress`
   * (`../cells/cell-store-address`) returns the whole routing as a value, and the
   * body below only applies it.
   *
   * Performs NO network IO — callers own their own POSTs. `actor` is the
   * writing surface (`null`: this data source). `clearCellLocal` is the
   * same chokepoint for removing a cell.
   */
  applyCellEditLocal(
    rowKey: RowNetworkKey,
    date: NaiveDate.Partial,
    data: LocalCellValue,
    cellId: Option<CellId>,
    actor: Option<Actor>,
  ): void {
    this.#paintLocalCell(rowKey, date, { clear: false, data }, cellId, actor);
  }

  /**
   * Remove a cell locally through the same chokepoint as
   * `applyCellEditLocal` (same routing, same notifications): every surface
   * rendering `rowKey` drops the cell at `date` (`cellId` names it on a
   * multi-cell row) without a refetch. No network I/O.
   */
  clearCellLocal(
    rowKey: RowNetworkKey,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
    actor: Option<Actor>,
  ): void {
    this.#paintLocalCell(rowKey, date, { clear: true }, cellId, actor);
  }

  #paintLocalCell(
    rowKey: RowNetworkKey,
    date: NaiveDate.Partial,
    write: LocalCellWrite,
    cellId: Option<CellId>,
    actor: Option<Actor>,
  ): void {
    const isCanonical = isCanonicalKey(rowKey);
    if (!isCanonical && this.#ordinaryStore != null && this.#resolveHandle != null) {
      const value = write.clear ? null : write.data;
      const writer = actor ?? SheetDataSource.ACTOR;
      const rowId = RowId.of(rowKey);
      const cache = this.#peekHandle(rowId);
      if (cache != null) {
        cache.applyCellWriteLocal(date, value, writer, cellId);
        return;
      }
      // No reader holds a cache for the row: the write lands in the store
      // alone, under the cardinality the store already declares for it (a
      // cache built later reads the store). A row the store has never held
      // still builds its cache, which knows the row type's cardinality.
      const localId = this.#idRegistry.resolve(RowKey.of(rowKey));
      const declared = this.#ordinaryStore.declaredCardinality(localId);
      if (declared != null) {
        CellStoreView.of("local", this.#ordinaryStore, localId, () => declared, null).set(date, value, cellId, writer);
        return;
      }
      this.#resolveHandle(rowId).applyCellWriteLocal(date, value, writer, cellId);
      return;
    }

    // ── Resolve ────────────────────────────────────────────────────────
    // The routing is decided ONCE, as a value. `matchStores` is
    // built alongside the matches and stays INDEX-ALIGNED with them (and so
    // with `plan.rows`), so the dispatch never re-derives a store from an
    // address: the address says WHERE, this array says WHICH object. It is a
    // list and not a map because two open sheets can carry the SAME row — one
    // `RowLocalId`, two distinct stores, both of which must receive the write.
    const matchStores: Option<SheetLocalCellStore>[] = [];
    const matches: CellStoreAddress.Match[] = [];
    for (const handle of this.sheetHandles.values()) {
      for (const row of handle.rows.peekAll()) {
        const lookupKey = row.lookupKey;
        if (row.id.toString() !== rowKey && lookupKey !== rowKey) continue;
        const localId = this.#idRegistry.resolve(RowKey.of(row.id.toString()));
        const rowsSheet = rowsSideSheetOf(handle);
        matchStores.push(rowsSheet ? sheetWiring(rowsSheet).localCells : null);
        matches.push({
          sheetId: handle.id,
          rowId: localId,
          hasSheetStore: rowsSheet != null,
        });
      }
    }
    const plan = resolveCellStoreAddress({
      rowKey,
      isCanonical,
      canonicalStoreAttached: this.#canonicalStore != null,
      // The canonical store needs the row's cell cardinality before its first
      // write (multi-card stores key cells by id); the rows-side descriptor of
      // any open matching row answers when there is one. Resolved only for a
      // canonical key — the scan walks every open sheet's rows, and no other
      // address consults it.
      declaredCardinality: isCanonical
        ? this.#resolveCellCardinality(rowKey)
        : null,
      hasCellId: cellId != null,
      matches,
      handleResolverAttached: this.#resolveHandle != null,
    });

    // ── Dispatch ───────────────────────────────────────────────────────
    // Order is part of the contract: canonical, then per matched row (its
    // store write immediately before its notification — `emit` is
    // synchronous), then the handle fallback. See `cell-store-address.ts`.

    // 1. Rows-side canonical store (client-shared; one write).
    const canonicalAddress = plan.canonical;
    const canonicalStore = this.#canonicalStore;
    if (canonicalAddress != null && canonicalStore != null) {
      const key = canonicalAddress.canonicalKey;
      canonicalStore.setCardinality(key, canonicalAddress.cardinality);
      if (write.clear) canonicalStore.clear(key, date, cellId, actor);
      else canonicalStore.set(key, date, write.data, cellId, null, actor);
    }

    // 2 + 4. Per-sheet matching rows: non-canonical store writes and
    // RowDataChanged emission. Each (sheet, row) pair is unique in
    // the loop (sheetHandles is keyed by stableId), so no dedup is
    // needed for the per-sheet sourceCellData writes.
    for (const [index, step] of plan.rows.entries()) {
      const address = step.store;
      const store = matchStores[index];
      if (address != null && store != null) {
        if (write.clear) store.clear(address.rowId, date, cellId, actor);
        else store.set(address.rowId, date, write.data, cellId, null, actor);
      }
      this.#sink.emit(new RowDataChanged(SheetDataSource.ACTOR, step.notify, null));
    }

    // 3. Fallback: when no rows-side store received the edit (no
    // rows accessor / no open sheet carries the row), write the
    // backend-registry RowCellCache directly so handle-side readers
    // still update. When a store WAS written, the store→handle
    // bridges deliver this same write — skipping here avoids a
    // double listener fire.
    const fallback = plan.fallback;
    const resolveHandle = this.#resolveHandle;
    if (fallback != null && resolveHandle != null) {
      const rh = resolveHandle(fallback.accessKey);
      rh.applyCellWriteLocal(
        date,
        write.clear ? null : write.data,
        actor ?? SheetDataSource.ACTOR,
        cellId,
      );
    }
  }

  /**
   * Server-read cell ingestion for sheet-scoped rows.
   *
   * `CellAccessor._bulkFetch` still updates `RowCellCache` for legacy fetch
   * coverage and sync bookkeeping. This method mirrors the same fetched cells
   * into the SDK 2.0 rows-side source store for non-canonical rows, making
   * `sourceCellData` the sheet-scoped home for fresh server reads too.
   */
  ingestServerFetchedCells(
    handle: SheetHandle,
    fetched: CellAccessor.BulkFetchedRow,
  ): void {
    this.#ingestServerFetchedCellsSince(handle, fetched, null, null, null);
  }

  /**
   * `handle`'s backend answers every read from a seed taken when it opened
   * (an ephemeral sheet's seeded backend): its answers are as of the open,
   * so a cell written into the rows store since then — a value or a clear —
   * stays as it is when one installs. Holds the store's revision at the open
   * until the returned release (the handle's teardown).
   */
  holdSeedRevision(handle: SheetHandle): () => void {
    const rowsSheet = rowsSideSheetOf(handle);
    if (!rowsSheet) return () => {};
    const store = sheetWiring(rowsSheet).localCells;
    const since = store.captureSeedRevision();
    this.#seedRevisions.set(handle, { store, since });
    return () => {
      if (this.#seedRevisions.get(handle)?.store !== store) return;
      this.#seedRevisions.delete(handle);
      store.releaseRevision(since);
    };
  }

  /**
   * Capture the rows store before the accessor parks a response behind its
   * gate, as the response to a sheet-wide read of `range` going out now: the
   * rows-side sheet's own row loads inside `range` wait for it, and every
   * sheet-local row it answers is read over `range` (`SheetWiring.beginSheetRead`;
   * `null`: the rows' loads do not hear of it).
   */
  captureServerFetchedCells(handle: SheetHandle, range: Option<NaiveDate.Range>): CellAccessor.BulkFetchObservation {
    const rowsSheet = rowsSideSheetOf(handle);
    const store = rowsSheet ? sheetWiring(rowsSheet).localCells : null;
    const sheetRead = rowsSheet && range != null ? sheetWiring(rowsSheet).beginSheetRead(range) : null;
    // A seeded backend's answer is as of the open (`holdSeedRevision`).
    const seed = this.#seedRevisions.get(handle) ?? null;
    const seeded = seed != null && seed.store === store ? seed.since : null;
    const since = seeded ?? store?.captureRevision() ?? null;
    const canonicalStore = this.#canonicalStore;
    const canonicalSince = canonicalStore?.captureRevision() ?? null;
    let released = false;
    // One response delivers many rows: its rows are matched through one index
    // of the handle's rows, built at the first delivery (after the ingestion
    // gate released, so it sees the rows as they are then) — O(fetched +
    // rows) per response instead of a scan of every row per fetched row.
    let index: Option<FetchedRowIndex> = null;
    return {
      receive: (fetched) => {
        if (released || rowsSideSheetOf(handle) !== rowsSheet) {
          throw new Error("Server-fetch observer lost its response-time rows store");
        }
        const canonicalKey = isCanonicalKey(fetched.lookupKey) ? fetched.lookupKey : isCanonicalKey(fetched.rowId) ? fetched.rowId : null;
        if (canonicalKey != null && canonicalStore != null && canonicalSince != null) {
          const cache = this.#resolveHandle?.(RowId.of(canonicalKey)) ?? null;
          cache?.view?.install(fetched.cells, canonicalSince);
          return;
        }
        index ??= FetchedRowIndex.of(handle.rows.peekAll());
        this.#ingestServerFetchedCellsSince(handle, fetched, since, index, sheetRead);
      },
      release: () => {
        if (released) return;
        released = true;
        sheetRead?.settle();
        // A seed revision is the handle's own hold, released at its teardown.
        if (store != null && since != null && seeded == null) store.releaseRevision(since);
        if (canonicalStore != null && canonicalSince != null) canonicalStore.releaseRevision(canonicalSince);
      },
    };
  }

  #ingestServerFetchedCellsSince(
    handle: SheetHandle,
    fetched: CellAccessor.BulkFetchedRow,
    since: Option<number>,
    index: Option<FetchedRowIndex>,
    sheetRead: Option<SheetWideRead>,
  ): void {
    if (fetched.cells.length === 0 && sheetRead == null) return;
    const rowsSheet = rowsSideSheetOf(handle);
    if (!rowsSheet) return;
    const lookupKey = fetched.lookupKey;
    if (lookupKey != null && isCanonicalKey(lookupKey)) return;
    if (isCanonicalKey(fetched.rowId)) return;

    const store = sheetWiring(rowsSheet).localCells;
    // The index narrows the rows to the ones a key of `fetched` names; each is
    // still checked against its keys as they are now, in list order.
    const candidates = index == null ? handle.rows.peekAll() : index.candidates(fetched);
    for (const row of candidates) {
      if (!SheetDataSource.#matchesFetchedRow(row.id.toString(), row.lookupKey, fetched)) {
        continue;
      }
      const localId = this.#idRegistry.resolve(RowKey.of(row.id.toString()));
      const source = rowsSheet.peekRow(localId);
      if (!(source instanceof SourceRow) || source.cellsDerived) continue;

      if (fetched.cells.length > 0) {
        store.setCardinality(
          localId,
          source.descriptor.cellCardinality === "multi" ? "multi" : "single",
        );
        // BulkFetchedRow already carries the accessor's canonical values.
        // Decode only at the wire entrance; repeating it loses hydrated fields.
        store.asInstall(() => store.batch(() => {
          for (const cell of fetched.cells) {
            SheetDataSource.#ingestServerFetchedCell(store, localId, cell, since, this.#dates);
          }
        }));
      }
      // The read answered this row over its range (cells or none): its own load issues nothing there.
      sheetRead?.cover(source);
    }
  }

  static #matchesFetchedRow(
    rowId: string,
    lookupKey: Option<string>,
    fetched: CellAccessor.BulkFetchedRow,
  ): boolean {
    if (rowId === fetched.rowId) return true;
    if (rowId === fetched.serverRowId.toString()) return true;
    return lookupKey != null && lookupKey === fetched.lookupKey;
  }

  static #ingestServerFetchedCell(
    store: SheetLocalCellStore,
    localId: RowLocalId,
    cell: RowData.Cell,
    since: Option<number>,
    dates: PartialDateMemo,
  ): void {
    const parsedDate = dates.parse(cell.date);
    const value = cell.data ?? null;
    if (parsedDate.isOk) {
      const date = parsedDate.exp("valid semantic cell date");
      if (since != null && store.writtenLocallySince(localId, since, date, cell.id ?? null)) return;
      // A cell the store already holds exactly changes nothing: the install
      // bracket (`asInstall`) skips it in the store (no event, no stale mark).
      store.set(
        localId,
        date,
        value,
        cell.id ?? null,
        null,
        SheetDataSource.SERVER_FETCH_ACTOR,
      );
      return;
    }

    if (cell.id == null) {
      throw new Error(
        `SheetDataSource.ingestServerFetchedCell: timepoint cell missing id ` +
          `(date="${cell.date.toString()}").`,
      );
    }
    if (since != null && store.customWrittenLocallySince(localId, since, cell.id)) return;
    const start = DateTime.fromRfc3339(cell.date.toString())
      .exp("valid timepoint cell start")
      .toUtc();
    const end = cell.endDate != null
      ? DateTime.fromRfc3339(cell.endDate.toString())
        .exp("valid timepoint cell end")
        .toUtc()
      : start;
    store.setCustom(localId, {
      id: cell.id,
      dtr: new DateTime.Range(start, end),
      value,
      style: null,
    }, SheetDataSource.SERVER_FETCH_ACTOR);
  }

  /**
   * Cell cardinality of `rowKey` per the rows-side descriptor of any
   * open matching row; null when no open sheet carries the row.
   *
   * Two lookups, because `handle.rows.peekAll()` holds only the sheet's own
   * TOP-LEVEL rows. A canonical row that reaches the sheet as a hydrated
   * alias child — every `:~{source}:{authId}:calendar:{calId}:events` row
   * under `alias.calendar-list` — is absent from that list, so the scan
   * misses and the caller falls back to guessing cardinality from the
   * presence of a `cellId`. That guess is wrong for exactly one lane: an
   * all-day (semantic-date) optimistic write carries no cellId, so the
   * events row's store was set to `single`, its cell stored date-keyed and
   * id-less, and the multi `list` aggregate above it then threw
   * `RowCellData.set: multi-cardinality row requires cellId` while
   * projecting that cell upward — out of `EventOps.insertOptimistic`,
   * before the create's POST. The event painted optimistically and was
   * never persisted. `:~lona:tasks` IS top-level, which is why tasks
   * resolved correctly and events did not.
   *
   * So ask the rows-side authority by lookup key too. Several views can
   * share one canonical key (the calendar body list and the all-day
   * strip), hence `peekAllByLookupKey`; they all hydrate from the same
   * wire type, so the first `SourceRow` answers for all of them.
   */
  #resolveCellCardinality(
    rowKey: RowNetworkKey,
  ): Option<CellCardinality> {
    for (const handle of this.sheetHandles.values()) {
      for (const row of handle.rows.peekAll()) {
        if (row.id.toString() !== rowKey && row.lookupKey !== rowKey) continue;
        const variant = handle.model?.peekRow(
          this.#idRegistry.resolve(RowKey.of(row.id.toString())),
        );
        const cardinality = variant == null ? null : sourceCardinalityOf(variant);
        if (cardinality != null) return cardinality;
      }
      const byKey = handle.model?.peekAllByLookupKey(rowKey) ?? [];
      for (const variant of byKey) {
        const cardinality = sourceCardinalityOf(variant);
        if (cardinality != null) return cardinality;
      }
    }
    return null;
  }

  // ── Invalidation ─────────────────────────────────────────────────────

  invalidateSheetRange(key: SheetKey): void {
    const handle = this.sheetHandles.get(key.mapKey);
    if (handle) this.#cellsOf(handle).resetFetchCache();
  }

  /** The full cell accessor `handle` was registered with. */
  #cellsOf(handle: SheetHandle): CellAccessor {
    return nonnull(
      this.#accessors.get(handle),
      "SheetDataSource: handle was not registered with its cell accessor",
    );
  }

  /** Notify that `column`'s data changed on every row: one `ColumnDataChanged(column, null)`. */
  invalidateColumn(column: NaiveDate.Partial): void {
    this.#sink.emit(new ColumnDataChanged(SheetDataSource.ACTOR, column, null));
  }

  removeRow(rowId: RowId, localId: Option<RowLocalId>): void {
    for (const handle of this.sheetHandles.values()) {
      this.#cellsOf(handle).removeRow(RowNetworkKey.of(rowId));
    }
    const lid =
      localId ?? this.#idRegistry.resolve(RowKey.of(rowId.toString()));
    if (lid) this.#sink.emit(new RowLegendChanged(SheetDataSource.ACTOR, lid, null, null));
  }

}

/**
 * A source row's cell cardinality, read off its reads face (`RowKind.of`:
 * `handle.model` hands out faces, never the live `SourceRow`); unset for any
 * other row.
 */
function sourceCardinalityOf(row: ModelRowReads): Option<CellCardinality> {
  const kind = RowKind.of(row);
  if (kind.role !== "source") return null;
  return kind.row.descriptor.cellCardinality === "multi" ? "multi" : "single";
}
